import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useLocation } from 'wouter';
import api from '../lib/api';
import {
  ArrowLeft, BookOpen, Video, FileText, HelpCircle,
  Lock, CheckCircle, XCircle, ChevronLeft, ChevronRight, Award,
} from 'lucide-react';

interface Module {
  id: string; title: string; content_type: string; content: string | null;
  order: number; duration_minutes: number | null; passing_score: number;
  prerequisite_module_id: string | null; is_active: boolean;
  completion_status: string | null; quiz_score: number | null;
  prerequisite_title: string | null;
}

interface Question {
  id: string; question_text: string; options: string[]; correct_index: number; order: number;
}

export default function TrainingModule({ pathId }: { pathId: string }) {
  const [, setLocation] = useLocation();
  const qc = useQueryClient();
  const [selectedModuleId, setSelectedModuleId] = useState<string | null>(null);

  const { data: modules, isLoading } = useQuery({
    queryKey: ['training-modules', pathId],
    queryFn: () => api.get(`/training/paths/${pathId}/modules`).then(r => r.data.data as Module[]),
  });

  const selectedModule = modules?.find(m => m.id === selectedModuleId) || null;

  if (!selectedModuleId || !selectedModule) {
    return (
      <div className="flex flex-col min-h-screen bg-gray-50">
        <header className="bg-indigo-600 text-white p-4">
          <div className="flex items-center gap-3">
            <button onClick={() => setLocation('/training')} className="p-1">
              <ArrowLeft className="h-6 w-6" />
            </button>
            <h1 className="text-lg font-bold">Módulos</h1>
          </div>
        </header>
        <div className="flex-1 p-4 space-y-2">
          {isLoading ? (
            <div className="flex justify-center py-12">
              <div className="h-8 w-8 rounded-full border-4 border-indigo-500 border-t-transparent animate-spin" />
            </div>
          ) : (
            modules?.map((mod) => {
              const isLocked = mod.prerequisite_module_id && mod.completion_status !== 'COMPLETED' &&
                modules.find(m => m.id === mod.prerequisite_module_id)?.completion_status !== 'COMPLETED';
              const TypeIcon = mod.content_type === 'VIDEO' ? Video : mod.content_type === 'PDF' ? FileText : mod.content_type === 'QUIZ' ? HelpCircle : BookOpen;
              return (
                <button
                  key={mod.id}
                  onClick={() => !isLocked && setSelectedModuleId(mod.id)}
                  disabled={!!isLocked}
                  className={`w-full bg-white rounded-xl border p-4 text-left flex items-center gap-3 ${isLocked ? 'opacity-50' : 'active:scale-[0.98]'} transition-transform`}
                >
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                    mod.completion_status === 'COMPLETED' ? 'bg-green-100' :
                    mod.completion_status === 'FAILED' ? 'bg-red-100' :
                    isLocked ? 'bg-gray-100' : 'bg-indigo-100'
                  }`}>
                    {isLocked ? <Lock className="h-5 w-5 text-gray-400" /> :
                     mod.completion_status === 'COMPLETED' ? <CheckCircle className="h-5 w-5 text-green-600" /> :
                     mod.completion_status === 'FAILED' ? <XCircle className="h-5 w-5 text-red-500" /> :
                     <TypeIcon className={`h-5 w-5 ${mod.content_type === 'QUIZ' ? 'text-green-600' : 'text-indigo-600'}`} />}
                  </div>
                  <div className="flex-1">
                    <h3 className="font-medium text-gray-900">{mod.order}. {mod.title}</h3>
                    <div className="flex items-center gap-2 mt-0.5">
                      <span className="text-xs text-gray-400">{mod.content_type}</span>
                      {mod.duration_minutes && <span className="text-xs text-gray-400">{mod.duration_minutes}min</span>}
                      {mod.quiz_score !== null && <span className="text-xs text-gray-500">Nota: {mod.quiz_score}%</span>}
                    </div>
                    {isLocked && mod.prerequisite_title && (
                      <p className="text-xs text-amber-600 mt-1">Complete: {mod.prerequisite_title}</p>
                    )}
                  </div>
                  <ChevronRight className="h-5 w-5 text-gray-300" />
                </button>
              );
            })
          )}
        </div>
        <div className="h-20" />
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-screen bg-gray-50">
      <header className="bg-indigo-600 text-white p-4">
        <div className="flex items-center gap-3">
          <button onClick={() => setSelectedModuleId(null)} className="p-1">
            <ArrowLeft className="h-6 w-6" />
          </button>
          <div>
            <h1 className="text-lg font-bold">{selectedModule.title}</h1>
            <p className="text-indigo-200 text-xs">{selectedModule.content_type}</p>
          </div>
        </div>
      </header>
      <div className="flex-1 p-4">
        {selectedModule.content_type === 'QUIZ' ? (
          <QuizPlayer moduleId={selectedModule.id} passingScore={selectedModule.passing_score} onDone={() => {
            qc.invalidateQueries({ queryKey: ['training-modules', pathId] });
            qc.invalidateQueries({ queryKey: ['training-progress'] });
            setSelectedModuleId(null);
          }} />
        ) : (
          <ContentViewer module={selectedModule} onComplete={() => {
            api.post(`/training/modules/${selectedModule.id}/start`).then(() => {
              qc.invalidateQueries({ queryKey: ['training-modules', pathId] });
              qc.invalidateQueries({ queryKey: ['training-progress'] });
              setSelectedModuleId(null);
            });
          }} />
        )}
      </div>
      <div className="h-20" />
    </div>
  );
}

function ContentViewer({ module, onComplete }: { module: Module; onComplete: () => void }) {
  return (
    <div className="space-y-4">
      {module.content_type === 'TEXT' && (
        <div className="bg-white rounded-xl border p-4">
          <p className="text-sm text-gray-700 whitespace-pre-wrap">{module.content || 'Sem conteúdo'}</p>
        </div>
      )}
      {module.content_type === 'VIDEO' && module.content && (
        <div className="bg-white rounded-xl border overflow-hidden">
          <div className="aspect-video">
            <iframe src={module.content} className="w-full h-full" allowFullScreen />
          </div>
        </div>
      )}
      {module.content_type === 'PDF' && module.content && (
        <div className="bg-white rounded-xl border p-4 text-center">
          <FileText className="h-12 w-12 mx-auto mb-3 text-red-500" />
          <p className="text-sm text-gray-600 mb-3">Documento PDF</p>
          <a href={module.content} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 bg-red-50 text-red-600 px-4 py-2 rounded-lg text-sm font-medium">
            Abrir PDF
          </a>
        </div>
      )}
      {module.completion_status !== 'COMPLETED' && (
        <button onClick={onComplete} className="w-full bg-indigo-600 text-white py-3 rounded-xl font-medium active:scale-[0.98] transition-transform">
          Marcar como Concluído
        </button>
      )}
      {module.completion_status === 'COMPLETED' && (
        <div className="flex items-center justify-center gap-2 text-green-600 py-3">
          <CheckCircle className="h-5 w-5" /> <span className="font-medium">Concluído</span>
        </div>
      )}
    </div>
  );
}

function QuizPlayer({ moduleId, passingScore, onDone }: { moduleId: string; passingScore: number; onDone: () => void }) {
  const [currentQ, setCurrentQ] = useState(0);
  const [answers, setAnswers] = useState<(number | null)[]>([]);
  const [result, setResult] = useState<any>(null);

  const { data: questions, isLoading } = useQuery({
    queryKey: ['training-questions', moduleId],
    queryFn: () => api.get(`/training/modules/${moduleId}/questions`).then(r => r.data.data as Question[]),
  });

  useEffect(() => {
    api.post(`/training/modules/${moduleId}/start`).catch(() => {});
  }, [moduleId]);

  useEffect(() => {
    if (questions) setAnswers(new Array(questions.length).fill(null));
  }, [questions]);

  const submit = useMutation({
    mutationFn: () => api.post(`/training/modules/${moduleId}/submit-quiz`, { answers }),
    onSuccess: (res) => setResult(res.data.data),
  });

  if (isLoading) return <div className="flex justify-center py-12"><div className="h-8 w-8 rounded-full border-4 border-indigo-500 border-t-transparent animate-spin" /></div>;
  if (!questions || questions.length === 0) return <p className="text-center text-gray-500 py-8">Nenhuma pergunta cadastrada</p>;

  if (result) {
    return (
      <div className="bg-white rounded-xl border p-6 text-center space-y-4">
        <div className={`w-20 h-20 rounded-full mx-auto flex items-center justify-center ${result.passed ? 'bg-green-100' : 'bg-red-100'}`}>
          {result.passed ? <CheckCircle className="h-10 w-10 text-green-600" /> : <XCircle className="h-10 w-10 text-red-500" />}
        </div>
        <h2 className="text-xl font-bold text-gray-900">{result.passed ? 'Aprovado!' : 'Reprovado'}</h2>
        <p className="text-3xl font-bold" style={{ color: result.passed ? '#16a34a' : '#ef4444' }}>{result.score}%</p>
        <p className="text-sm text-gray-500">{result.correct_answers}/{result.total_questions} corretas (mínimo {passingScore}%)</p>
        {result.certified && (
          <div className="flex items-center justify-center gap-2 text-yellow-600 bg-yellow-50 p-3 rounded-lg">
            <Award className="h-5 w-5" /> <span className="font-medium">Certificado obtido!</span>
          </div>
        )}
        <button onClick={onDone} className="w-full bg-indigo-600 text-white py-3 rounded-xl font-medium active:scale-[0.98] transition-transform">
          Voltar
        </button>
      </div>
    );
  }

  const question = questions[currentQ];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between text-sm text-gray-500">
        <span>Pergunta {currentQ + 1} de {questions.length}</span>
        <span>{answers.filter(a => a !== null).length} respondidas</span>
      </div>
      <div className="h-1.5 bg-gray-200 rounded-full overflow-hidden">
        <div className="h-full bg-indigo-500 rounded-full transition-all" style={{ width: `${((currentQ + 1) / questions.length) * 100}%` }} />
      </div>
      <div className="bg-white rounded-xl border p-4">
        <h3 className="font-medium text-gray-900 mb-4">{question.question_text}</h3>
        <div className="space-y-2">
          {question.options.map((opt: string, i: number) => (
            <button
              key={i}
              onClick={() => { const n = [...answers]; n[currentQ] = i; setAnswers(n); }}
              className={`w-full text-left p-3 rounded-lg border-2 transition-colors ${
                answers[currentQ] === i ? 'border-indigo-500 bg-indigo-50 text-indigo-700' : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <span className="font-medium mr-2">{String.fromCharCode(65 + i)})</span> {opt}
            </button>
          ))}
        </div>
      </div>
      <div className="flex gap-3">
        {currentQ > 0 && (
          <button onClick={() => setCurrentQ(currentQ - 1)} className="flex-1 flex items-center justify-center gap-2 border rounded-xl py-3 text-gray-600">
            <ChevronLeft className="h-4 w-4" /> Anterior
          </button>
        )}
        {currentQ < questions.length - 1 ? (
          <button onClick={() => setCurrentQ(currentQ + 1)} className="flex-1 flex items-center justify-center gap-2 bg-indigo-600 text-white rounded-xl py-3 font-medium">
            Próxima <ChevronRight className="h-4 w-4" />
          </button>
        ) : (
          <button
            onClick={() => { if (answers.every(a => a !== null)) submit.mutate(); }}
            disabled={answers.some(a => a === null) || submit.isPending}
            className="flex-1 bg-green-600 text-white rounded-xl py-3 font-medium disabled:opacity-50"
          >
            {submit.isPending ? 'Enviando...' : 'Enviar Respostas'}
          </button>
        )}
      </div>
    </div>
  );
}
